import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics';

export const buildLoginRedirectPath = (fullPath) => {
  return `/sales?showLogin=1&successUrl=${encodeURIComponent(window.location.origin + fullPath)}`;
};

export const getCloneAgentTargetId = (cloneResult) =>
  cloneResult?.conversationId || cloneResult?.aiStrategyId;

/**
 * Builds the clone request body. A private share link (/shared/strategy/<token>)
 * is authorized by its token alone; the server finds the strategy and pinned
 * version from it, so no internal ids are sent. Legacy shares send the strategy
 * id plus the legacy share token.
 */
function buildCloneRequestBody({
  strategy,
  aiStrategyId,
  shareToken,
  privateLinkToken,
  privateLinkPassword,
}) {
  if (privateLinkToken) {
    const body = { privateLinkToken };
    if (privateLinkPassword) body.privateLinkPassword = privateLinkPassword;
    return body;
  }
  if (!strategy?.id) {
    throw new Error('Strategy ID is required to clone strategy.');
  }
  const body = { strategyId: strategy.id, aiStrategyId };
  if (shareToken) body.shareToken = shareToken;
  return body;
}

/**
 * Copies a shared strategy into the signed-in user's account.
 * @param {Object} options
 * @param {{ id?: string, name?: string | null } | null} [options.strategy] Legacy share subject.
 * @param {string | null} [options.aiStrategyId]
 * @param {string | null} [options.shareToken] Legacy share token.
 * @param {string | null} [options.privateLinkToken] Token from /shared/strategy/<token>.
 * @param {string | null} [options.privateLinkPassword] Password for a protected private link.
 * @param {() => Promise<string | null>} options.getApiToken
 */
export async function cloneSharedStrategy({
  strategy,
  aiStrategyId,
  shareToken,
  privateLinkToken,
  privateLinkPassword,
  getApiToken,
}) {
  let token;
  try {
    token = await getApiToken();
  } catch {
    token = null;
  }

  if (!token) {
    return { loginRequired: true, cloneResult: null };
  }

  const body = buildCloneRequestBody({
    strategy,
    aiStrategyId,
    shareToken,
    privateLinkToken,
    privateLinkPassword,
  });

  const response = await fetch(`${process.env.REACT_APP_API_URL}/ai-bot-builder/clone-strategy`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    // Share pages carry the link token in their URL; never pass it on as a Referer.
    referrerPolicy: 'no-referrer',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(errorData.message || 'Failed to clone strategy. Please try again.');
    // Callers branch on the server's code (for example PAYMENT_REQUIRED), never on message text.
    error.code = errorData.code || null;
    error.status = response.status;
    throw error;
  }

  const cloneResult = await response.json();
  pushEvent({
    event: ANALYTICS_EVENTS.STRATEGY_CLONED,
    originalStrategyId: strategy?.id || null,
    originalAiStrategyId: aiStrategyId || null,
    clonedStrategyId: cloneResult.strategyId,
    clonedAiStrategyId: cloneResult.aiStrategyId,
    strategyName: strategy?.name || null,
    source: privateLinkToken ? 'private_share_link' : 'legacy_share',
  });

  return { loginRequired: false, cloneResult };
}
